import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { AssignmentGroupingType } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import classNames from 'classnames'
import { BreakoutUserAvatarStack } from 'components/breakout/BreakoutUserAvatar'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { ChatBubbleIcon } from 'components/icons/ChatBubble'
import { CircleAlert } from 'components/icons/CircleAlert'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LeaveRoomDialog } from './dialogs/LeaveRoomDialog'
import { RoomsDialog } from './dialogs/RoomsDialog'
import { RoomStateFormDialogV2 } from './dialogs/RoomStateFormDialogV2'
import { ScheduleRoomStateButton } from './dialogs/ScheduleRoomStateButton'
import {
  ScheduledDayOfMonthOnYear,
  ScheduledDayOfWeekAtTime,
} from './ScheduledTimeParts'
import { StudentAssignmentChatDialog } from 'pages/student/assignment/dialogs/GroupChatDialog'

const ExistingRoomV2 = observer(function StudentAssignmentStep1() {
  const cubit = useStudentAssignmentCubit()
  const { showDialog, popDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const currentUser = useBreakoutUser()

  const isStudentInRoom = cubit.roomState.users.some(
    (u) => u.id === currentUser.uid
  )

  const mustScheduleRoom = cubit.libraryObject.scheduledAtDate === undefined

  let disableLeaveRoom = false
  let disableEditRoom = false
  let disableScheduleRoom = false

  let disabledLeaveTooltip: string = ''
  let disabledEditTooltip: string = ''
  let disabledScheduleTooltip: string = ''

  if (cubit.assignment.data.forbidStudentGroupSelection) {
    disableLeaveRoom = true
    disabledLeaveTooltip =
      tt.student_assignment.leave_group_disabled_by_instructor()
  } else if (cubit.studentGraded) {
    disableLeaveRoom = true
    disabledLeaveTooltip = tt.student_assignment.leave_group_disabled()
  }

  if (cubit.assignment.data.forbidStudentGroupScheduling) {
    disableEditRoom = true
    disabledEditTooltip =
      tt.student_assignment.edit_group_disabled_by_instructor()

    disableScheduleRoom = true

    disabledScheduleTooltip =
      tt.student_assignment.schedule_group_disabled_by_instructor()
  }

  return (
    <div className="mb-2 w-full text-center">
      <AutomaticRandomGroupingHeader />
      <div className="flex w-full flex-grow-0 flex-col items-center justify-center gap-3">
        <div className="flex flex-row gap-5">
          <div
            data-testid="student-assignment-schedule-area"
            className="flex justify-items-end"
          >
            <div className="text-center">
              <h2 className="mb-1 text-title-large">
                {cubit.roomState.roomStateName}
              </h2>

              <div
                data-testid="group-member-avatars"
                className="flex flex-row justify-center"
              >
                <BreakoutUserAvatarStack
                  radius={12}
                  spacing={-2}
                  users={cubit.roomState.users}
                />
              </div>
            </div>
          </div>
          <BreakoutTooltip
            enabled={!mustScheduleRoom && !!cubit.roomState.data.scheduledAt}
            content={tt.student_assignment.your_local_timezone({
              timezone: DateTime.local().toFormat('ZZZZ'),
            })}
          >
            <div className="flex flex-col text-center">
              {!mustScheduleRoom && (
                <>
                  <p
                    className="text-title-large"
                    data-testid="student-assignment-scheduled"
                  >
                    <ScheduledDayOfWeekAtTime
                      scheduledAt={cubit.roomState.data.scheduledAt}
                      tt={tt}
                    />
                  </p>
                  <div className="flex flex-grow items-center justify-center text-center">
                    <p className="text-body-medium text-on-surface-var">
                      <ScheduledDayOfMonthOnYear
                        scheduledAt={cubit.roomState.data.scheduledAt}
                      />
                    </p>
                  </div>
                </>
              )}
              {/* what if room is not scheduled but there is a group leader? */}
              {mustScheduleRoom && cubit.currentUserCanScheduleRoom && (
                <ScheduleRoomStateButton
                  useInlineDialog={false}
                  disabled={disableScheduleRoom}
                  kind={disableScheduleRoom ? 'secondary' : 'accent'}
                  size="small"
                  tooltip={disableScheduleRoom ? disabledScheduleTooltip : ''}
                  cubit={cubit}
                  className="my-auto"
                  loadingText={tt.student_assignment.set_meeting_time()}
                  icon={
                    <TriangleAlertIcon
                      size={15}
                      role="img"
                      aria-label={tt.student_assignment.set_meeting_time()}
                    />
                  }
                >
                  {tt.student_assignment.set_meeting_time()}
                </ScheduleRoomStateButton>
              )}
              {mustScheduleRoom && !cubit.currentUserCanScheduleRoom && (
                <div>
                  <h2 className="mb-auto mt-auto text-title-large">
                    {tt.student_assignment.join_dialog_v2.not_scheduled()}
                  </h2>
                </div>
              )}
            </div>
          </BreakoutTooltip>
        </div>
        <div>
          <ChatButton cubit={cubit} />
          <div className="mt-2 flex flex-row items-center justify-center gap-1 text-body-small text-on-surface-var">
            <BreakoutTooltip
              enabled={disableEditRoom}
              content={disabledEditTooltip}
            >
              <button
                className={classNames('py-1.5 underline', {
                  'text-on-surface-disabled': disableEditRoom,
                })}
                data-testid="edit-group-button"
                disabled={disableEditRoom}
                onClick={(e) => {
                  showDialog(
                    () => (
                      <RoomStateFormDialogV2
                        cubit={cubit}
                        onSave={() => popDialog()}
                      />
                    ),
                    { trigger: e.currentTarget }
                  )
                }}
              >
                {tt.student_assignment.join_dialog_v2.edit_group()}
              </button>
            </BreakoutTooltip>
            {isStudentInRoom && (
              <>
                <strong className="font-semibold">&#8226;</strong>
                <BreakoutTooltip
                  enabled={disableLeaveRoom}
                  content={disabledLeaveTooltip}
                >
                  <button
                    data-testid="leave-group-button"
                    disabled={disableLeaveRoom}
                    className={classNames('py-1.5 underline', {
                      'text-on-surface-disabled': disableLeaveRoom,
                    })}
                    onClick={(e) => {
                      showDialog(
                        () => (
                          <LeaveRoomDialog
                            leaveRoom={async () => {
                              currentUser.notifications.markAssignmentAsRead(
                                cubit.sectionId,
                                cubit.assignmentId
                              )

                              await cubit.leaveRoom()
                            }}
                          />
                        ),
                        { trigger: e.currentTarget }
                      )
                    }}
                  >
                    {tt.student_assignment.leave_group()}
                  </button>
                </BreakoutTooltip>
              </>
            )}
          </div>
          {cubit.hasMultipleRooms && (
            <div className="mt-1 text-body-small text-on-surface-var">
              {tt.student_assignment.switch_to()}{' '}
              {cubit.switchableRoomStates.map((room, index, arr) => (
                <React.Fragment key={room.id}>
                  <button
                    className="underline"
                    onClick={() => cubit.changeRoomStateId(room.id)}
                  >
                    {room.roomStateName}
                  </button>
                  {index < arr.length - 1 && ', '}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

const ChatButton = observer(function ChatButton({
  cubit,
}: {
  cubit: StudentAssignmentCubit
}) {
  const user = useBreakoutUser()
  const { showDialog } = useDialogs()
  const { t } = useTranslation()
  const { tt } = useTranslationTyped()

  const notificationsCount =
    user.notifications.getChatNotificationsForRoomCount(cubit.roomStateId ?? '')

  return (
    <BreakoutButton
      kind="secondary"
      size="small"
      badgeCount={notificationsCount || undefined}
      badgeSrLabel={tt.library.unread_messages()}
      disabled={!(cubit.userIsInCurrentRoom || cubit.user.isCorre)}
      onClick={(e) => {
        showDialog(() => <StudentAssignmentChatDialog cubit={cubit} />, {
          trigger: e.currentTarget,
        })
      }}
      icon={<ChatBubbleIcon size={15} />}
    >
      {t('student_assignment.chat')}
    </BreakoutButton>
  )
})

const AutomaticRandomGroupingHeader = observer(
  function StudentAssignmentStep1() {
    const cubit = useStudentAssignmentCubit()
    const { t } = useTranslation()
    if (
      cubit.assignment.groupingType !== AssignmentGroupingType.automaticRandom
    ) {
      return null
    }
    return (
      <div className="mb-2 flex justify-center">
        <div className="flex items-center rounded-full bg-core-secondary px-4 py-2 text-body-medium text-core-on-secondary">
          <span className="mr-1">
            <CircleAlert size={15} />
          </span>
          {t('student_assignment.uses_automatic_randomized_grouping')}
        </div>
      </div>
    )
  }
)

const NeedsARoom = observer(function NeedsARoom() {
  const cubit = useStudentAssignmentCubit()
  const { showDialog } = useDialogs()
  const { tt } = useTranslationTyped()

  const currentUser = useBreakoutUser()

  // Admins can always see the room dialog
  const canChangeRoom =
    currentUser.isCorre ||
    cubit.currentUserIsInSection ||
    cubit.currentUserIsInstructor ||
    currentUser.isAssistantOfInstructor(cubit.section.data.instructorUserId)

  const selectionDisabled = cubit.assignment.data.forbidStudentGroupSelection

  if (selectionDisabled) {
    return (
      <div data-testid="group-placement-in-progress">
        <div className="mt-4 text-body-medium text-grey-text">
          {tt.student_assignment.step_1()}
        </div>
        <h2 className="mt-3 text-title-large">
          {tt.student_assignment.group_placement_in_progress()}
        </h2>
        <div className="min-h-[80px] overflow-hidden">
          <p className="mx-auto mt-1 max-w-[23rem] text-body-medium">
            {tt.student_assignment.group_placement_in_progress_description()}
          </p>
        </div>
      </div>
    )
  }

  if (
    cubit.assignment.groupingType === AssignmentGroupingType.automaticRandom
  ) {
    return (
      <div className="mb-2 w-full py-5 text-center">
        <AutomaticRandomGroupingHeader />
        <div className="text-body-medium text-grey-text">
          {tt.student_assignment.step_1()}
        </div>
        <h2 className="mt-3 text-title-large">
          {tt.student_assignment.group_tbd()}
        </h2>

        <div className="min-h-[80px] overflow-hidden">
          <p className="mx-auto mt-1 max-w-[23rem] text-body-medium">
            {tt.student_assignment.group_tbd_description()}
          </p>
          <BreakoutButton
            data-testid="join-group-button"
            onClick={(e) => {
              showDialog(() => <RoomsDialog cubit={cubit} />, {
                trigger: e.currentTarget,
              })
            }}
            className="w-50 mt-4 inline-block rounded-2xl bg-core-secondary px-8 py-2 text-label-large text-core-on-secondary outline-none"
          >
            {tt.student_assignment.manually_join_group()}
          </BreakoutButton>
        </div>
      </div>
    )
  } else {
    const createDisabled = cubit.assignment.data.forbidStudentGroupCreation
    const isSolo = cubit.assignment.maxGroupSize === 1

    let groupDescription: string
    if (createDisabled) {
      groupDescription = tt.student_assignment.join_only_group_description()
    } else if (isSolo) {
      groupDescription =
        tt.student_assignment.solo_assignment_group_description()
    } else {
      groupDescription =
        tt.student_assignment.join_or_create_group_description()
    }

    return (
      <div className="text-center">
        <div className="mt-4 text-body-medium text-grey-text">
          {tt.student_assignment.step_1()}
        </div>
        <h2 className="mt-3 text-title-large">
          {createDisabled
            ? tt.student_assignment.join_only_group()
            : tt.student_assignment.join_or_create_group()}
        </h2>

        <div className="min-h-[80px] overflow-hidden">
          <p className="mx-auto mt-1 max-w-[23rem] text-body-medium">
            {groupDescription}
          </p>
          {canChangeRoom && (
            <BreakoutButton
              kind="primary"
              size="large"
              data-testid="join-group-button"
              onClick={(e) => {
                showDialog(() => <RoomsDialog cubit={cubit} />, {
                  trigger: e.currentTarget,
                })
              }}
              className="mb-1 mt-4"
            >
              {tt.student_assignment.join_group()}
            </BreakoutButton>
          )}
        </div>
      </div>
    )
  }
})

export const StudentAssignmentStep1 = observer(function StudentAssignmentStep1(
  props: React.HTMLAttributes<HTMLDivElement>
) {
  const cubit = useStudentAssignmentCubit()

  return (
    <div {...props}>
      {cubit.roomStateId && <ExistingRoomV2 />}
      {!cubit.roomStateId && <NeedsARoom />}
    </div>
  )
})
